import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Row, Col, Typography, Spin } from 'antd';
import { apiService } from '../../services/api';
import { AdvertisementType, CategoryResponse } from '../../types';
import { useNavigateWithScroll } from '../../hooks/useNavigateWithScroll';
import AdvertisementDisplay from '../../components/Common/AdvertisementDisplay';

const { Title, Text } = Typography;

const Categories: React.FC = () => {
  const navigate = useNavigateWithScroll();


  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await apiService.getCategories();
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    staleTime: 1000 * 60 * 10, // 10 dakika
  });

  const handleCategoryClick = (categoryName: string) => {
    navigate(`/mentor-search?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <div className="app-page">
      <div className="app-container">
        <div className="app-hero">
          <div className="app-hero-inner">
            <Title level={1} className="app-hero-title">Kategoriler</Title>
            <p className="app-hero-subtitle">Aradığınız kategorilerde alanında uzman eğitmenlerden destek alın. Yeteneklerinizi geliştirin.</p>
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <AdvertisementDisplay pagePath="/categories" type={AdvertisementType.Banner} position="top" />
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Row gutter={[24, 24]}>
            {categories.map((category: CategoryResponse) => (
              <Col xs={24} sm={12} md={8} lg={6} key={category.id}>
                <Card 
                  hoverable
                  className="app-card"
                  style={{ textAlign: 'center', height: '100%', cursor: 'pointer' }}
                  bodyStyle={{ padding: '24px' }}
                  onClick={() => handleCategoryClick(category.name)}
                >
                  <div style={{ 
                    width: '120px',
                    height: '120px',
                    margin: '0 auto 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {category.categoryImage ? (
                      <img
                        src={`data:image/jpeg;base64,${category.categoryImage}`}
                        alt={category.name}
                        style={{ 
                          maxWidth: '100%', 
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '8px'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '2rem',
                        color: '#999'
                      }}>
                        {category.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <Title level={4} style={{ 
                    fontSize: '1.2rem',
                    marginBottom: '8px',
                    color: 'var(--text-primary)'
                  }}>
                    {category.name}
                  </Title>
                  {category.description && (
                    <Text style={{ 
                      fontSize: '0.9rem',
                      color: '#666',
                      display: 'block'
                    }}>
                      {category.description}
                    </Text>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <div style={{ marginTop: '24px' }}>
          <AdvertisementDisplay pagePath="/categories" type={AdvertisementType.Card} position="inline" />
        </div>
      </div>
    </div>
  );
};

export default Categories;
